/**
 * The admin's `maintenance_banner` text, shown across the top of every page for everyone, signed in
 * or not (`GET /site-settings/public`, apps/api/src/settings/setting-defs.ts: «Message shown across
 * the top of the site for everyone. Empty means no banner.»). It was served and read by nothing.
 *
 * Looks like the unverified-email strip (`#verify-email-banner`: same tokens, no new colours) and,
 * like it, takes its height out of the page rather than covering it: while a banner is up the app
 * root is moved down by that height (`html.has-maintenance-banner`, style.css) — fixed editor chrome
 * is positioned against the app root, so it moves with it. With no text nothing is added and no class
 * is set, so a site without a banner is exactly as it was.
 *
 * Best-effort: a failed lookup is «no banner». Re-read when the tab becomes visible again, so a
 * banner the admin sets or clears reaches an open tab without a reload.
 */
import { fetchPublicSiteSettings, maintenanceBannerText } from "../api/site-settings-api";

const BANNER_ID = "maintenance-banner";
const ROOT_CLASS = "has-maintenance-banner";

/** Applies `text` (null removes the banner). Exported for the test; the DOM is the only state. */
export function renderMaintenanceBanner(text: string | null, doc: Document = document): void {
  const root = doc.documentElement;
  let el = doc.getElementById(BANNER_ID);
  if (text === null) {
    el?.remove();
    root.classList.remove(ROOT_CLASS);
    root.style.removeProperty("--maintenance-h");
    return;
  }
  if (!el) {
    el = doc.createElement("div");
    el.id = BANNER_ID;
    el.setAttribute("role", "status");
    doc.body.insertBefore(el, doc.body.firstChild);
  }
  el.textContent = text; // text, never HTML — it is typed by an admin
  root.classList.add(ROOT_CLASS);
  root.style.setProperty("--maintenance-h", `${el.getBoundingClientRect().height}px`);
}

export function startMaintenanceBanner(): void {
  const refresh = (): void => {
    void fetchPublicSiteSettings().then((s) => renderMaintenanceBanner(maintenanceBannerText(s.maintenanceBanner)));
  };
  refresh();
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") refresh();
  });
  // The text can wrap onto more lines when the window narrows: keep the published height true.
  window.addEventListener("resize", () => {
    const el = document.getElementById(BANNER_ID);
    if (el) document.documentElement.style.setProperty("--maintenance-h", `${el.getBoundingClientRect().height}px`);
  });
}
